import { ENVIRONMENTS } from '@shared/utils';

/**
 * @group Constants
 * 
 * @summary
 * <blockquote>Sentry configuration defaults for logging</blockquote>
 * 
 * @example
 * ```typescript
 * import { SENTRY_CONFIG } from '@shared/logger/sentry/constants';
 * import { ENVIRONMENTS } from '@shared/utils';
 * 
 * const sampleRate = SENTRY_CONFIG.getTracesSampleRate(ENVIRONMENTS.PRODUCTION);
 * ```
 */
export const SENTRY_CONFIG = {
  /**
   * Environment-specific traces sample rates for performance monitoring.
   * Production uses lower rate to manage billing and data volume.
   * Development uses 100% for complete visibility during debugging.
   */
  TRACES_SAMPLE_RATE: {
    DEVELOPMENT: 1.0,  // 100% sampling for full visibility
    PREVIEW: 0.5,      // 50% sampling for reasonable visibility
    PRODUCTION: 0.2,   // 20% sampling to balance monitoring with billing
  },
  
  /**
   * Default traces sample rate for performance monitoring
   */
  DEFAULT_TRACES_SAMPLE_RATE: 1.0,
  
  /**
   * Default replays session sample rate
   */
  DEFAULT_REPLAYS_SESSION_SAMPLE_RATE: 0.1,
  
  /**
   * Default replays on error sample rate
   */
  DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE: 1.0,
  
  /**
   * Enable Sentry debug mode (verbose console logging)
   * Set to false to prevent verbose integration installation messages
   */
  DEBUG: false,

  /**
   * Get the appropriate traces sample rate for a given environment.
   * Falls back to production rate for unknown environments.
   * 
   * @param environment - The environment string (use ENVIRONMENTS constants)
   * @returns The traces sample rate (0.0 to 1.0)
   * 
   * @example
   * ```typescript
   * import { ENVIRONMENTS } from '@shared/utils';
   * const sampleRate = SENTRY_CONFIG.getTracesSampleRate(ENVIRONMENTS.PRODUCTION); // Returns 0.2
   * ```
   */
  getTracesSampleRate(environment: string): number {
    switch (environment) {
      case ENVIRONMENTS.DEVELOPMENT:
        return SENTRY_CONFIG.TRACES_SAMPLE_RATE.DEVELOPMENT;
      case ENVIRONMENTS.PREVIEW:
        return SENTRY_CONFIG.TRACES_SAMPLE_RATE.PREVIEW;
      case ENVIRONMENTS.PRODUCTION:
        return SENTRY_CONFIG.TRACES_SAMPLE_RATE.PRODUCTION;
      default:
        return SENTRY_CONFIG.TRACES_SAMPLE_RATE.PRODUCTION;
    }
  },
} as const;

